import { Autocomplete, Avatar, Box, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import voiceService from '../../../services/appointments/calls';

interface Props {
  value?: number;
  filterType: 'assigneeId' | 'coreProviderId';
  handleFilterChanges: (filter: { [filter: string]: number | undefined }) => void;
}

export default function AgentsFilter({ value, filterType, handleFilterChanges }: Props) {
  const [agentOptions, setAgentOptions] = useState<
    { id: number; name: string; coreProviderId: number | null }[]
  >([]);
  const [agentFilter, setAgentFilter] = useState<string>('');

  const notify = useNotify();

  const fetchDirectory = useCallback(async () => {
    try {
      const directory = await voiceService.getAgentDirectory();
      setAgentOptions(
        Object.values(directory.data || {}).map((entry) => ({
          id: Number(entry.id),
          name: entry.name,
          coreProviderId: Number(entry.coreProviderId),
        })),
      );
    } catch (error) {
      notify('Failed to fetch agent directory', 'error');
    }
  }, [voiceService]);

  const extractOptionValue = (
    option: { id: number; name: string; coreProviderId: number | null } | null,
  ) => {
    if (!option) {
      return undefined;
    }

    if (filterType === 'assigneeId') {
      return option.id;
    }
    return option.coreProviderId || undefined;
  };

  const agentMatchesFilter = (
    filterString: string,
    agent: { id: number; name: string; coreProviderId: number | null },
  ) => {
    return (
      agent.name.toLowerCase().includes(filterString.toLowerCase()) ||
      agent.id.toString().includes(filterString.toLowerCase()) ||
      agent.coreProviderId?.toString().includes(filterString.toLowerCase())
    );
  };

  useEffect(() => {
    fetchDirectory();
  }, []);

  return (
    <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
      <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>
        {filterType === 'assigneeId' ? 'Assignee' : 'Agent'}
      </Typography>
      <Autocomplete
        fullWidth
        className='member-selector'
        options={
          agentFilter
            ? agentOptions.filter((option) => agentMatchesFilter(agentFilter, option))
            : agentOptions
        }
        filterOptions={(x) => x}
        getOptionLabel={(option) => `${option.name} ID: ${option.coreProviderId}`}
        onChange={(_, v) => handleFilterChanges({ [filterType]: extractOptionValue(v) })}
        noOptionsText='No agents found'
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        value={agentOptions.find((p) => extractOptionValue(p) === value) || null}
        renderInput={(params) => (
          <TextField
            {...params}
            variant='outlined'
            placeholder='Enter an agent name or ID'
            onChange={(e) => setAgentFilter(e.target.value.toLowerCase())}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Avatar sx={{ color: 'action.active', mr: 1 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component='li' {...props}>
            <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
              {`${option.name?.[0] ?? ''}${option.name?.[1] ?? ''}`.toUpperCase()}
            </Avatar>
            <Box>
              <Typography fontSize={'0.8rem'}>{option.name}</Typography>
              <Typography variant='caption' color='textSecondary'>
                ID: {option.coreProviderId}
              </Typography>
            </Box>
          </Box>
        )}
      />
    </Box>
  );
}
